import { toast } from 'react-toastify';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import {
  getTokensFromLocalStorage,
  removeTokensFromLocalStorage,
  saveTokensToLocalStorage,
} from '../../Functions/LocalStorageUtils';
import {
  setMaintenance,
  setTokens,
  userLogout,
  validateSession,
} from '../reducers/authReducer';
import { Role } from '../types/auth';

interface MaintenanceResponseI {
  success?: boolean;
  message?: string;
  eta?: string;
}

const getMaintenanceEta = (error: unknown): string | null | undefined => {
  const queryError = (error as { error?: FetchBaseQueryError })?.error;

  if (!queryError || !('status' in queryError) || queryError.status !== 503) {
    return undefined;
  }

  return (queryError.data as MaintenanceResponseI | undefined)?.eta ?? null;
};

interface ValidateSessionResponseI {
  success?: boolean;
  valid?: boolean;
  userId?: number;
  email?: string;
  name?: string;
  company?: string;
  phone?: string;
  role?: string;
  tokens?: {
    access_token: string;
    refresh_token: string;
  }
}

export const authApi = createApi({
  reducerPath: 'authQuery',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_URL,
    credentials: 'include',
    prepareHeaders: (headers) => {

      const tokens = getTokensFromLocalStorage();

      if (tokens?.access_token) {
        headers.set('Authorization', `Bearer ${tokens.access_token}`);
      }

      return headers;
    }
  }),
  tagTypes: [],
  endpoints: (builder) => ({
    checkUserTokens: builder.mutation<{ access_token: string; refresh_token: string }, void>({
      query: () => ({
        url: `/v1/auth/refresh`,
        method: 'POST',
        body: {
          refresh_token: getTokensFromLocalStorage().refresh_token
        },
        headers: {
          Authorization: `Bearer ${getTokensFromLocalStorage().access_token}`
        }
      }),
      async onQueryStarted(arg: void, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          saveTokensToLocalStorage({
            access_token: data.access_token,
            refresh_token: data.refresh_token
          });
          dispatch(
            setTokens({
              access_token: data.access_token,
              refresh_token: data.refresh_token
            })
          );
        } catch (error) {
          console.error(error);
        }
      }
    }),
    logOut: builder.mutation<null, void>({
      query: () => ({
        url: `/v1/auth/logout`,
        method: 'POST',
        body: {}
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }){
        try {
          toast.info('Logging out...');
          await queryFulfilled;
          removeTokensFromLocalStorage();
          dispatch(userLogout());
          toast.success('Logout successful', {
            onClose: () => (window.location.pathname = '/'),
            autoClose: 3000
          });
        }catch(error){
          console.error(error);
          toast.error('Logout failed');
        }
      }
    }),
    validateSession: builder.mutation<ValidateSessionResponseI, void>({
      query: () => ({
        url: `/v1/auth/validate-session`,
        method: 'GET',
        xhrFields: { withCredentials: true }
      }),
      async onQueryStarted(arg: void, { dispatch, queryFulfilled}){
        try {
          const { data } = await queryFulfilled;
          if(!data.valid) return;
          dispatch(validateSession({
            email: data.email || '',
            name: data.name || '',
            company: data.company || '',
            phone: data.phone || '',
            role: data.role as Role || 'user',
          }));
          if(data.tokens){
            dispatch(setTokens({
              access_token: data.tokens.access_token,
              refresh_token: data.tokens.refresh_token
            }));
            saveTokensToLocalStorage({
              access_token: data.tokens.access_token,
              refresh_token: data.tokens.refresh_token
            });
          }
        } catch (error) {
          const eta = getMaintenanceEta(error);
          if (eta !== undefined) {
            dispatch(setMaintenance({ active: true, eta }));
            return;
          }
          console.error(error);
        }
      }
    })
  })
});

export const {
  useCheckUserTokensMutation,
  useLogOutMutation,
  useValidateSessionMutation
} = authApi;
